import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { MobileBlocker } from '../../../components/mobile-blocker';
import { PageBlockerProduct } from '../../../components/page-blocker/page-blocker-product';
import { Pagination } from '../../../components/pagination';
import { type Product, type ProductDisplayStatus } from '../../../entity/api/product';
import { type ProductSortAndFilter } from '../../../entity/api/routes/product';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import {
  useAuthUser,
  useOrganizationBilling,
  useProductsByBrandId,
  useRemoveProductBulk,
  useUpdateProduct,
  useUpdateProductBulk,
  useUsersByIds,
} from '../../../services/storage';
import { getSelectedBrand } from '../../../shared/brand';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { useDebouncedEffect } from '../../../shared/hooks/use-debounce';
import { getSelectedOrganization } from '../../../shared/organization';
import { CreateProductModal } from './components/-create-product-modal';
import { HeaderProducts } from './components/-header-products';
import { SingleProductListing } from './components/-product-listing';

const PRODUCTS_PER_PAGE = 20;

// URL Search params type
type ProductsSearch = {
  page?: number;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt';
  sortByDir?: 'ASC' | 'DESC';
};

export const Route = createFileRoute('/app/_sidebar-layout/products/')({
  validateSearch: function (search: Record<string, unknown>): ProductsSearch {
    // Validate page: must be a positive integer >= 1
    let validatedPage = 1;
    if (typeof search.page === 'number') {
      const pageNum = Math.floor(search.page);
      validatedPage = Math.max(pageNum, 1);
    }

    return {
      page: validatedPage,
      search: typeof search.search === 'string' ? search.search : undefined,
      sortBy:
        search.sortBy === 'createdAt' || search.sortBy === 'updatedAt' ? search.sortBy : undefined,
      sortByDir: search.sortByDir === 'ASC' ? 'ASC' : 'DESC',
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  // TanStack Query hooks
  const { data: currentUser } = useAuthUser();
  const currentOrg = getSelectedOrganization(currentUser);
  const currentBrand = getSelectedBrand(currentUser);
  const navigate = useNavigate();
  const search = Route.useSearch();

  // URL is the source of truth
  const page = search.page ?? 1;
  const searchTerm = search.search ?? '';
  const sortBy = search.sortBy ?? 'createdAt';
  const sortByDir = search.sortByDir ?? 'DESC';

  // Only local state: input typing UX
  const [searchInput, setSearchInput] = useState(searchTerm);

  // Build query params from URL only
  const queryParams: ProductSortAndFilter = useMemo(
    function () {
      return {
        offset: (page - 1) * PRODUCTS_PER_PAGE,
        limit: PRODUCTS_PER_PAGE,
        search: searchTerm.length > 0 ? searchTerm : undefined,
        sortBy: sortBy === 'updatedAt' ? 'updatedAt' : 'createdAt',
        sortByDir,
      };
    },
    [page, searchTerm, sortBy, sortByDir]
  );

  // TanStack Query for products with pagination
  const productsQuery = useProductsByBrandId(currentBrand?._id, queryParams);
  const products = useMemo(
    function () {
      return productsQuery.data?.products ?? [];
    },
    [productsQuery.data?.products]
  );
  const totalCount = productsQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PRODUCTS_PER_PAGE));

  // TanStack Query for organization billing
  const { data: billingStatus } = useOrganizationBilling(currentOrg?._id);

  // TanStack Query mutations
  const updateProductMutation = useUpdateProduct();
  const updateProductBulkMutation = useUpdateProductBulk();
  const deleteProductBulkMutation = useRemoveProductBulk();

  // Extract unique user IDs from products for author fetching
  const productAuthorIds = useMemo(
    function () {
      const ids = products.map((p) => p.createdBy).filter((id): id is string => id != null);
      return [...new Set(ids)]; // deduplicate
    },
    [products]
  );

  // TanStack Query hook for fetching authors by IDs
  const { data: authorsData } = useUsersByIds(productAuthorIds);
  const authorsList = authorsData ?? [];

  const [isModalProductOpen, setIsModalProductOpen] = useState(false);

  function toggleModalProduct() {
    setIsModalProductOpen(!isModalProductOpen);
  }

  function updateProductStatus(productId: string, status: ProductDisplayStatus) {
    const product = products.find(function (p) {
      return p._id === productId;
    });
    if (product == null) {
      return;
    }
    if (status === product.displayStatus) {
      return;
    }
    const updatedProduct = {
      ...product,
      displayStatus: status,
    };
    updateProductMutation.mutate(
      { product: updatedProduct },
      {
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/index.tsx',
              method: 'updateProductStatus',
              infoMessage: 'unable to update product status',
            },
            trackError
          );
        },
      }
    );
  }

  function handleSortDirChange(dir: 'asc' | 'desc') {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          sortByDir: dir === 'desc' ? 'DESC' : 'ASC',
          page: 1,
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.products/index.tsx',
          method: 'handleSortDirChange',
          infoMessage: 'Failed to update sortByDir',
        },
        trackError
      );
    });
  }

  function handlePageChange(page: number) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return { ...prev, page };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.products/index.tsx',
          method: 'handlePageChange',
          infoMessage: 'Failed to navigate to page',
        },
        trackError
      );
    });
  }

  function handleUpdateInBulk(productsToUpdate: Product[]) {
    updateProductBulkMutation.mutate(
      { products: productsToUpdate },
      {
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/index.tsx',
              method: 'handleUpdateInBulk',
              infoMessage: 'unable to update products',
            },
            trackError
          );
        },
      }
    );
  }

  function handleDeleteSelected(productIds: string[]) {
    if (productIds.length === 0) {
      return;
    }
    deleteProductBulkMutation.mutate(productIds, {
      onError: function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.products/index.tsx',
            method: 'handleDeleteSelected',
            infoMessage: 'unable to delete products',
          },
          trackError
        );
      },
    });
  }

  // Keep input in sync when user uses back/forward
  useEffect(
    function () {
      setSearchInput(searchTerm);
    },
    [searchTerm]
  );

  // Debounce only search -> push to URL
  useDebouncedEffect(
    function () {
      if (searchInput === searchTerm) {
        return;
      }

      navigate({
        from: Route.fullPath,
        search: function (prev) {
          return {
            ...prev,
            search: searchInput.trim().length > 0 ? searchInput.trim() : undefined,
            page: 1,
          };
        },
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.products/index.tsx',
            method: 'useDebouncedEffect[searchInput]',
            infoMessage: 'Failed to update search in URL',
          },
          trackError
        );
      });
    },
    [searchInput, searchTerm, navigate],
    500
  );

  // Redirect to valid page if current page is out of bounds
  useEffect(
    function () {
      if (productsQuery.isLoading) {
        return;
      }
      if (page < 1 || page > totalPages) {
        navigate({
          from: Route.fullPath,
          search: function (prev) {
            return {
              ...prev,
              page: Math.min(Math.max(page, 1), totalPages),
            };
          },
          replace: true,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/index.tsx',
              method: 'useEffect[pageValidation]',
              infoMessage: 'Failed to redirect to valid page',
            },
            trackError
          );
        });
      }
    },
    [page, totalPages, productsQuery.isLoading, navigate]
  );

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT) || currentBrand?.productType !== 'eCommerce') {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/_sidebar-layout.products/index.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate, currentBrand?.productType]
  );

  // When brand changes, refetch products using TanStack Query's built-in refetch
  // The useProductsByBrandId hook automatically refetches when brandId changes
  // No manual useEffect needed - TanStack Query handles this

  return (
    <>
      <MobileBlocker />
      <div className="relative h-full w-full flex-1 overflow-y-auto">
        <div className="mx-auto flex h-full w-full max-w-content flex-col items-center">
          <HeaderProducts
            filteredProducts={products}
            currentUser={currentUser}
            sortByDir={sortByDir}
            onSortDirChange={handleSortDirChange}
            search={searchInput}
            onSearchChange={setSearchInput}
            isSearchAvailable={true}
            isSortByAvailable={true}
          />
          <SingleProductListing
            products={products}
            onCreateProduct={toggleModalProduct}
            authorList={authorsList}
            updateProductStatus={updateProductStatus}
            handleDeleteSelected={handleDeleteSelected}
            handleUpdateInBulk={handleUpdateInBulk}
          />

          {/* Pagination */}
          {totalPages > 1 ? (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="py-8"
            />
          ) : null}
          <CreateProductModal isOpen={isModalProductOpen} onOpenChange={setIsModalProductOpen} />
          <PageBlockerProduct organizationBilling={billingStatus} />
        </div>
      </div>
    </>
  );
}
